import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Modal } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import styles from './DocumentViewModal.module.css';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.cMapUrl = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`;
pdfjs.GlobalWorkerOptions.cMapPacked = true;
pdfjs.GlobalWorkerOptions.standardFontDataUrl = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`;

const DocumentViewModal = ({ file, setFile }) => {
    const [numPages, setNumPages] = useState(0);
    const [rasterImages, setRasterImages] = useState({});

    useEffect(() => {
        setRasterImages({});
    }, [file]);

    const onDocumentLoadSuccess = async ({ numPages }) => {
        setNumPages(numPages ?? 0);
    };

    const rasterizePage = useCallback(async (pageNum) => {
        try {
            if (!file) return;

            const isStringSource = typeof file === 'string';

            let source;
            if (isStringSource) {
                source = { url: file };
            } else if (file instanceof ArrayBuffer) {
                source = { data: file };
            } else if (file instanceof Uint8Array) {
                source = { data: file };
            } else if (file && typeof file.arrayBuffer === 'function') {
                source = { data: await file.arrayBuffer() };
            } else if (file?.data) {
                source = { data: file.data };
            } else {
                source = { data: file };
            }
            const loadingTask = pdfjs.getDocument({
                ...source,
                cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                cMapPacked: true,
                standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
            });
            const doc = await loadingTask.promise;
            const page = await doc.getPage(pageNum);
            const viewport1x = page.getViewport({ scale: 1 });
            const desiredWidth = 520;
            const scaleFactor = desiredWidth / viewport1x.width;
            const viewport = page.getViewport({ scale: scaleFactor });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const dpr = window.devicePixelRatio || 1;
            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            await page.render({ canvasContext: context, viewport, intent: 'print' }).promise;
            const dataUrl = canvas.toDataURL('image/png');
            setRasterImages((prev) => ({ ...prev, [pageNum]: dataUrl }));
        } catch (error) {
            console.error('Rasterize failed', error);
        }
    }, [file]);

    return (
        <Modal
            open={file ? true : false}
            sx={{ '& .MuiBackdrop-root': { backgroundColor: '#13214440' } }}
            closeAfterTransition
        >
            <Box className={styles.new_limit_wrap}>
                <IconButton
                    className={styles.close_btn}
                    onClick={() => setFile('')}
                >
                    <CloseIcon sx={{ "&:hover": { color: "#ed4c78" } }} />
                </IconButton>
                <Box className={numPages >= 2 ? styles.doc_viewer_wrapper : styles.doc_viewer_wrapper_}>
                    <Document
                        file={file}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => console.error('PDF load error', error)}
                        options={{
                            cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                            cMapPacked: true,
                            standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
                            useSystemFonts: true
                        }}
                    >
                        {Array.from({ length: numPages }, (_, index) => index + 1).map((pages, i) => (
                            <Box key={i} className={styles.doc_pdf_viewer}>
                                {!rasterImages[pages] && (
                                    <Page
                                        width={520}
                                        pageNumber={pages}
                                        renderAnnotationLayer
                                        renderTextLayer
                                        renderMode="canvas"
                                        onRenderError={() => rasterizePage(pages)}
                                    />
                                )}
                                {rasterImages[pages] && (
                                    <img
                                        src={rasterImages[pages]}
                                        alt={`page-${pages}`}
                                        style={{ width: 520, display: 'block' }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Document>
                </Box>
            </Box>
        </Modal>
    )
};

export default DocumentViewModal;